import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { makeStyles } from '@material-ui/core';
import { CreateOrderBasicTextField, CreateOrderFormSection } from './styles';
import {
  CapaQuotationUserType,
  CreatePurchaseOrderInput,
  Maybe
} from '@generated/graphql';
import { colorSet, clsx } from '@components/Provider';
import { CustomizedButton, CustomizedTypography } from '@components/Customized';

interface CreateOrderUserInfoProps {
  userType: CapaQuotationUserType;
  organizationName?: Maybe<string>;
  companyRegistrationNumber?: string;
  managerName?: string;
  isWithdrawn: boolean;
}

interface EditMode {
  managerName: boolean;
  contact: boolean;
  email: boolean;
}

const CreateOrderUserInfo: React.FC<CreateOrderUserInfoProps> = ({
  userType,
  organizationName,
  companyRegistrationNumber,
  managerName,
  isWithdrawn
}) => {
  const initialEditSetting = {
    managerName: false,
    contact: false,
    email: false
  };
  const [isOnEdit, setIsOnEdit] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<EditMode>(initialEditSetting);
  const [newManagerName, setNewManagerName] = useState<string>(
    managerName ?? ''
  );
  const { editOkIcon, editBox } = useCreateOrderUserInfoStyles();
  const isClient = userType === CapaQuotationUserType.Client;
  const { register, setValue } = useFormContext<CreatePurchaseOrderInput>();

  useEffect(() => {
    if (Object.entries(editMode).some((el) => el[1] === true)) {
      setIsOnEdit(true);
    } else {
      setIsOnEdit(false);
    }
  }, [editMode]);

  return (
    <CreateOrderFormSection>
      <CustomizedTypography variant="h4" fontWeight="500" gutterBottoms={8}>
        {isClient ? '발주처' : '수주처'} 정보
      </CustomizedTypography>

      {organizationName && (
        <>
          <div className="entry">
            <div className="entryTitle">상호</div>
            <div className="entryContent">{organizationName}</div>
          </div>
          <div className="entry">
            <div className="entryTitle">등록번호</div>
            <div className="entryContent">{companyRegistrationNumber}</div>
          </div>
        </>
      )}

      {!organizationName && (
        <div className="entry">
          {isClient && (
            <input
              disabled={isWithdrawn}
              type="hidden"
              ref={register}
              name="managerName"
            />
          )}
          <div className="entryTitle">담당자명</div>
          <div className="editableContent">
            {isOnEdit && editMode.managerName ? (
              <CreateOrderBasicTextField
                disabled={isWithdrawn}
                defaultValue={managerName}
                onChange={(e: any) => {
                  setNewManagerName(e.currentTarget.value);
                }}
              />
            ) : (
              <div className={clsx(['entryContent', { isClient }])}>
                {managerName}
              </div>
            )}
            {isClient && !editMode.managerName && (
              <CustomizedButton
                disabled={isWithdrawn}
                variant="text"
                onClick={() => {
                  if (!editMode.managerName) {
                    setEditMode({ ...initialEditSetting, managerName: true });
                  } else {
                    setValue('managerName', newManagerName);
                    setEditMode(initialEditSetting);
                  }
                }}
              >
                <CustomizedTypography variant="body1" color="primary">
                  수정
                </CustomizedTypography>
              </CustomizedButton>
            )}
            {isClient && editMode.managerName && (
              <div className={editBox}>
                <CustomizedButton
                  variant="text"
                  disabled={isWithdrawn}
                  onClick={() => {
                    setEditMode(initialEditSetting);
                  }}
                >
                  <CustomizedTypography variant="body1" color="textSecondary">
                    취소
                  </CustomizedTypography>
                </CustomizedButton>
                <CustomizedButton
                  disabled={isWithdrawn}
                  variant="text"
                  className={editOkIcon}
                  onClick={() => {
                    setValue('managerName', newManagerName);
                    setEditMode(initialEditSetting);
                  }}
                >
                  <CustomizedTypography variant="body1" color="primary">
                    저장
                  </CustomizedTypography>
                </CustomizedButton>
              </div>
            )}
          </div>
        </div>
      )}
    </CreateOrderFormSection>
  );
};

export default CreateOrderUserInfo;

const useCreateOrderUserInfoStyles = makeStyles((theme) => ({
  editBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  editOkIcon: {
    '& svg': {
      fill: colorSet.primary500
    }
  }
}));
